/**
 * Shared vocabulary for the tracking pipeline.
 *
 * Coordinate spaces used across the app (see `camera/videoCoordinates.ts` for the
 * conversion pipeline that connects them):
 *
 *  - **image**   MediaPipe normalized landmark space: x,y in [0,1] relative to the raw
 *                camera frame, origin top-left. z is a relative depth in roughly the same
 *                scale as x, negative towards the camera.
 *  - **display** Normalized coordinates of the on-screen video/canvas box: x,y in [0,1],
 *                origin top-left, already mirrored/cropped/rotated as the user sees it.
 *  - **screen**  Isotropic normalized screen space: origin at the centre of the canvas,
 *                +x right, +y up, y in [-0.5, 0.5] and x in [-aspect/2, aspect/2].
 *                Distances in this space are proportional to on-screen pixels, so a
 *                "distance" means the same thing horizontally and vertically.
 *
 * Everything the interaction layer consumes is expressed in **screen** space.
 */

export interface Vector3 {
  x: number;
  y: number;
  z: number;
}

export interface Point2 {
  x: number;
  y: number;
}

export type Handedness = 'Left' | 'Right' | 'Unknown';

/** Number of landmarks MediaPipe reports for one hand. */
export const HAND_LANDMARK_COUNT = 21;

/** MediaPipe hand-landmark indices we care about. */
export const LANDMARK = {
  WRIST: 0,
  THUMB_MCP: 2,
  THUMB_TIP: 4,
  INDEX_MCP: 5,
  INDEX_TIP: 8,
  MIDDLE_MCP: 9,
  MIDDLE_TIP: 12,
  RING_MCP: 13,
  RING_TIP: 16,
  PINKY_MCP: 17,
  PINKY_TIP: 20,
} as const;

/** Landmarks averaged to derive a stable palm centre. */
export const PALM_LANDMARKS: readonly number[] = [
  LANDMARK.WRIST,
  LANDMARK.INDEX_MCP,
  LANDMARK.MIDDLE_MCP,
  LANDMARK.RING_MCP,
  LANDMARK.PINKY_MCP,
];

/** The four finger tips used to measure how open the hand is (the thumb is too mobile). */
export const FINGER_TIPS: readonly number[] = [
  LANDMARK.INDEX_TIP,
  LANDMARK.MIDDLE_TIP,
  LANDMARK.RING_TIP,
  LANDMARK.PINKY_TIP,
];

/** Every landmark that terminates a digit; drawn larger by the skeleton overlay. */
export const TIP_LANDMARKS: readonly number[] = [LANDMARK.THUMB_TIP, ...FINGER_TIPS];

/** The five landmarks outlining the palm, in winding order, for the skeleton's palm fill. */
export const PALM_OUTLINE: readonly number[] = [
  LANDMARK.WRIST,
  LANDMARK.INDEX_MCP,
  LANDMARK.MIDDLE_MCP,
  LANDMARK.RING_MCP,
  LANDMARK.PINKY_MCP,
];

/** Pairs of landmark indices forming the hand skeleton, used by the debug overlay. */
export const HAND_CONNECTIONS: readonly (readonly [number, number])[] = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 4],
  [0, 5],
  [5, 6],
  [6, 7],
  [7, 8],
  [5, 9],
  [9, 10],
  [10, 11],
  [11, 12],
  [9, 13],
  [13, 14],
  [14, 15],
  [15, 16],
  [13, 17],
  [17, 18],
  [18, 19],
  [19, 20],
  [0, 17],
];

/** One landmark as produced by MediaPipe (image-normalized). */
export interface NormalizedLandmark {
  x: number;
  y: number;
  z: number;
  visibility?: number;
}

/** A single detected hand for one inference pass, still in image space. */
export interface HandLandmarkSample {
  /** 21 landmarks in image-normalized coordinates. */
  landmarks: NormalizedLandmark[];
  /** Optional metric landmarks relative to the hand's own origin (hand-relative, not room). */
  worldLandmarks?: NormalizedLandmark[];
  handedness: Handedness;
  /** Handedness classification score in [0,1]. */
  score: number;
}

/** Result of one hand-landmarker inference pass. */
export interface InferenceResult {
  hands: HandLandmarkSample[];
  /** `performance.now()`-based timestamp of the source video frame. */
  timestamp: number;
  /** Wall-clock cost of the inference call in milliseconds. */
  inferenceMs: number;
  frameWidth: number;
  frameHeight: number;
}

/**
 * Normalized, smoothed, identity-stable representation of one hand, in **screen** space.
 * This is the only hand type the interaction/audio layers are allowed to consume.
 */
export interface TrackedHand {
  id: string;
  handedness: Handedness;
  confidence: number;
  /** Palm centre, averaged from the palm landmarks. */
  palm: Vector3;
  indexTip: Vector3;
  thumbTip: Vector3;
  middleTip: Vector3;
  wrist: Vector3;
  /** Palm velocity in screen units per second. */
  velocity: Vector3;
  /**
   * All 21 landmarks, smoothed, in **screen** space — the same points the skeleton overlay
   * draws. Reused between frames like the rest of `TrackedHand`, so copy before retaining.
   */
  landmarks: readonly Point2[];
  /**
   * How open the hand is: 0 is a closed fist, 1 is a fully spread hand. Scale-invariant, so
   * it does not change as the player moves towards or away from the camera. Smoothed harder
   * than position because it drives timbre rather than pitch.
   */
  openness: number;
  /** Palm size in screen units (wrist to middle knuckle); used as a matching cue. */
  span: number;
  /** False while the hand is being coasted through a brief detection dropout. */
  visible: boolean;
  /** `performance.now()` timestamp of the most recent observation. */
  timestamp: number;
}

/** Which landmark drives the instrument. */
export type ControlPointMode = 'palm' | 'indexTip';

export function createVector3(x = 0, y = 0, z = 0): Vector3 {
  return { x, y, z };
}

export function copyVector3(target: Vector3, source: Vector3): Vector3 {
  target.x = source.x;
  target.y = source.y;
  target.z = source.z;
  return target;
}

/** Distance in the x/y plane only — z from MediaPipe is not metric and must not gate control. */
export function distance2d(a: Point2, b: Point2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.hypot(dx, dy);
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Ratio of mean fingertip reach to palm length at which the hand reads as fully closed /
 * fully open. Measured from MediaPipe output: a fist sits near 1.15, a flat spread hand near
 * 2.0. The band is deliberately narrower than the extremes so both ends are reachable without
 * straining.
 */
const OPENNESS_CLOSED_RATIO = 1.25;
const OPENNESS_OPEN_RATIO = 1.9;

/**
 * How open a hand is, from its landmarks, in 0..1.
 *
 * Uses the mean distance from the wrist to the four finger tips, divided by the palm length
 * (wrist to middle knuckle). Dividing by the hand's own size is what makes the reading
 * independent of how far the player is from the camera — an absolute distance would read
 * "closed" simply because the player stepped back.
 *
 * Returns 0 for degenerate input rather than throwing: this runs per frame on model output.
 */
export function handOpenness(points: readonly Point2[]): number {
  const wrist = points[LANDMARK.WRIST];
  const middleMcp = points[LANDMARK.MIDDLE_MCP];
  if (!wrist || !middleMcp) return 0;
  const palmLength = Math.hypot(middleMcp.x - wrist.x, middleMcp.y - wrist.y);
  if (!(palmLength > 1e-5)) return 0;

  let reach = 0;
  let counted = 0;
  for (const index of FINGER_TIPS) {
    const tip = points[index];
    if (!tip) continue;
    reach += Math.hypot(tip.x - wrist.x, tip.y - wrist.y);
    counted += 1;
  }
  if (counted === 0) return 0;

  const ratio = reach / counted / palmLength;
  return normalizeRange(ratio, OPENNESS_CLOSED_RATIO, OPENNESS_OPEN_RATIO);
}

/** Palm length (wrist to middle knuckle) in whatever space `points` are expressed in. */
export function handSpan(points: readonly Point2[]): number {
  const wrist = points[LANDMARK.WRIST];
  const middleMcp = points[LANDMARK.MIDDLE_MCP];
  if (!wrist || !middleMcp) return 0;
  return Math.hypot(middleMcp.x - wrist.x, middleMcp.y - wrist.y);
}

/** Maps a value from [inMin, inMax] to [0,1], clamped. Returns 0 for a degenerate range. */
export function normalizeRange(value: number, inMin: number, inMax: number): number {
  const span = inMax - inMin;
  if (!Number.isFinite(span) || Math.abs(span) < 1e-9) return 0;
  return clamp01((value - inMin) / span);
}
